import { AxiosError } from 'axios'
import uniq from 'lodash/uniq'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { distinctUntilChanged, Subscription } from 'rxjs'
import { func, pattern, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import Role from 'iam/models/Role'
import { SearchableUser } from 'iam/models/SearchableUser'
import Skill from 'iam/models/Skill'
import Team from 'iam/models/Team'
import TeamRole from 'iam/models/TeamRole'
import Workspace from 'iam/models/Workspace'
import RoleStore from 'iam/RoleStore'
import SkillStore from 'iam/SkillStore'
import TeamStore from 'iam/TeamStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import ModalViewModel from 'infra/ModalViewModel'
import { Id, Nullable } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'
import { pluralize } from 'utils/pluralization-util'
import MemberStore from '../../../iam/MemberStore'
import { Portal as DtoPortal, UserSource } from '../../../iam/dtos/member'
import { match } from 'utils/pattern-match-util'
import { SearchableUserDTO } from 'iam/dtos/searchable-user'
import { Collection } from 'infra/collection'

interface InviteResults {
  email: string
  success?: boolean
  error?: AxiosError
}

/**
 * List of portals the workspace invitation can link to.
 */
export enum Portal {
  CustomerPortal = 'Customer Portal',
  WorkPortal = 'Work Portal',
}

export class WorkspaceInvitationViewModel extends ModalViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The list of workspace roles.
   */
  @observable
  workspaceRoles: Role[] = []

  /**
   * The list of available skills.
   */
  @observable
  availableSkills: Skill[] = []

  /**
   * The list of teams.
   */
  @observable
  teams: Team[] = []

  /**
   * The list of team roles.
   */
  @observable
  teamRoles: TeamRole[] = []

  /**
   * The list of portals.
   */
  @observable
  readonly portals = Object.values(Portal)

  /**
   * The current content of the email address text area.
   */
  @observable
  emailAddressTextArea = ''

  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Nullable<Workspace> = null

  /**
   * The currently selected workspace role.
   */
  @observable
  workspaceRole: Nullable<Role> = null

  /**
   * The currently selected skills.
   */
  @observable
  skills: Skill[] = []

  /**
   * The currently selected team.
   */
  @observable
  team: Nullable<Team> = null

  /**
   * The currently selected team role.
   */
  @observable
  teamRole: Nullable<TeamRole> = null

  /**
   * The currently selected portal.
   */
  @observable
  portal: Portal = Portal.CustomerPortal

  /**
   * Validation rules for workspace invitations.
   */
  validation = validationContext(this, {
    emailAddressTextArea: [
      func(
        () => this.emailAddresses.length > 0,
        'You must enter at least one valid email address.',
      ),
    ],
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    workspaceRole: [
      func(
        () => this.workspaceRole !== null,
        'You must select a workspace role.',
      ),
    ],
    teamRole: [
      func(
        () => this.team === null || this.teamRole !== null,
        'If you select a team, you must also select a team role.',
      ),
    ],
  })

  /**
   * Stream of changes to the invitation form inputs.
   */
  private invitationSelections$ = toStream(() => this.workspace, false).pipe(
    distinctUntilChanged(),
    tapAction((workspace) => {
      if (workspace) {
        this.clearSelections()
        this.updateOptions()
      }
    }),
  )

  constructor(
    private _selectedWorkspace: () => Workspace,
    private _selectedUsers: () => Set<Id>,
    private _loadedUsers: () => SearchableUser[],
    private _storedUsers: () => Collection<SearchableUserDTO, SearchableUser>,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private roleStore: RoleStore,
    private skillStore: SkillStore,
    private teamStore: TeamStore,
    private teamRoleStore: TeamRoleStore,
    private memberStore: MemberStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Activate the view model.
   */
  activate() {
    this.subscription = this.invitationSelections$.subscribe()
  }

  /**
   * Deactivate the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
  }

  /**
   * The currently selected workspace.
   */
  @computed
  get selectedWorkspace(): Nullable<Workspace> {
    return this._selectedWorkspace() === Workspace.none
      ? null
      : this._selectedWorkspace()
  }

  /**
   * The currently selected users.
   */
  @computed
  get selectedUsers() {
    return this._selectedUsers()
  }

  /**
   * The list of all users.
   */
  @computed
  get users() {
    return this._loadedUsers()
  }

  /**
   * The list of all users.
   */
  @computed
  get storedUsers() {
    return this._storedUsers()
  }

  /**
   * The list of workspaces available to the user.
   */
  @computed
  get workspaces() {
    const availableWorkspaces = this.getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.hasPermission('ADMIN_USER_LIST'))
      .map((mb) => mb.workspace)

    return availableWorkspaces
  }

  /**
   * A list of valid email addresses extracted from email address text area.
   */
  @computed
  get emailAddresses() {
    return uniq(
      this.emailAddressTextArea
        .split(/[,|\n]/)
        .map((e) => e.trim().toLowerCase())
        .filter(
          (e) =>
            !!e &&
            validationContext(
              { email: e },
              { email: [pattern('email')] },
            ).validate().isValid,
        ),
    )
  }

  /**
   * Set the list of workspace roles.
   */
  @action.bound
  setWorkspaceRoles(roles: Role[]) {
    this.workspaceRoles = roles
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the list of teams.
   */
  @action.bound
  setTeams(teams: Team[]) {
    this.teams = teams
  }

  /**
   * Set the list of team roles.
   */
  @action.bound
  setTeamRoles(roles: TeamRole[]) {
    this.teamRoles = roles
  }

  /**
   * Set the email addresses text area.
   */
  @action.bound
  setEmailAddressTextArea(emails: string) {
    this.emailAddressTextArea = emails
  }

  /**
   * Set the workspace field.
   */
  @action.bound
  setWorkspace(workspace: Nullable<Workspace>) {
    this.workspace = workspace
  }

  /**
   * Set the workspace role field.
   */
  @action.bound
  setWorkspaceRole(role: Nullable<Role>) {
    this.workspaceRole = role
  }

  /**
   * Set the skills field.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = skills
  }

  /**
   * Set the team field.
   */
  @action.bound
  setTeam(team: Nullable<Team>) {
    this.team = team
  }

  /**
   * Set the team role field.
   */
  @action.bound
  setTeamRole(role: Nullable<TeamRole>) {
    this.teamRole = role
  }

  /**
   * Set the portal field.
   */
  @action.bound
  setPortal(portal: Portal) {
    this.portal = portal
  }

  /**
   * Update the input options if a workspace is changed.
   */
  @action.bound
  async updateOptions() {
    if (!this.workspace) {
      return
    }

    await this.roleStore.find({ workspace_id: Id.assert(this.workspace.id) })
    await this.skillStore.find({ workspace_id: Id.assert(this.workspace.id) })
    await this.teamStore.findTeams({
      workspace_id: Id.assert(this.workspace.id),
    })
    await this.teamRoleStore.find({
      workspace_id: Id.assert(this.workspace.id),
    })

    this.setWorkspaceRoles(
      this.roleStore.forWorkspace(Id.assert(this.workspace.id)),
    )
    this.setAvailableSkills(
      this.skillStore.forWorkspace(Id.assert(this.workspace.id)),
    )
    this.setTeams(
      this.teamStore.teams.items.filter(
        // We already established that a workspace is selected.
        (t) => t.workspace.id === this.workspace!.id,
      ),
    )
    this.setTeamRoles(
      this.teamRoleStore.forWorkspace(Id.assert(this.workspace.id)),
    )
  }

  /**
   * Clears selections when the workspace is changed.
   */
  @action.bound
  clearSelections() {
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)
  }

  /**
   * Initializes the input options.
   */
  @action.bound
  async initialize() {
    if (this.selectedWorkspace !== null) {
      this.setWorkspace(this.selectedWorkspace)

      this.updateOptions()
    }
  }

  /**
   * Opens the dialog with the contextual workspace.
   */
  @override
  show() {
    this.initialize()
    super.show()
  }

  /**
   * Opens the dialog with the contextual user.
   */
  @action.bound
  showWithUser(user: SearchableUser) {
    this.initialize()
    this.setEmailAddressTextArea(user.email)
    super.show()
  }

  /**
   * Opens the dialog with all the selected users.
   */
  @action.bound
  showWithSelections() {
    this.initialize()
    const selectedUserEmailList = this.storedUsers
      .filter((u) => this.selectedUsers.has(u.publicId))
      .map((u) => u.email)
      .join(', ')
    this.setEmailAddressTextArea(selectedUserEmailList)
    super.show()
  }

  /**
   * Clears workspace invitation dialog state.
   */
  @action.bound
  reset() {
    this.setWorkspaceRoles([])
    this.setAvailableSkills([])
    this.setTeams([])
    this.setTeamRoles([])

    this.setEmailAddressTextArea('')
    this.setWorkspace(null)
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)
    this.setPortal(Portal.CustomerPortal)
  }

  /**
   * Send workspace invitations to the specified users.
   */
  @action.bound
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const results: InviteResults[] = await Promise.all(
      this.emailAddresses.map((e) =>
        this.memberStore
          .createManually({
            email: e,
            workspace_id: Id.assert(this.workspace!.id),
            role_id: this.workspaceRole?.id,
            skill_ids: this.skills.map((s) => Id.assert(s.id)),
            team_id: this.team?.id,
            team_role_id: this.teamRole?.id,
            source: UserSource.Local,
            portal: match(this.portal, {
              [Portal.WorkPortal]: () => DtoPortal.WorkPortal,
              [Portal.CustomerPortal]: () => DtoPortal.CustomerPortal,
            }),
          })
          .then(() => {
            return { email: e, success: true }
          })
          .catch((err) => {
            return { email: e, error: err }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    // If all invitations succeeded, close dialog.
    if (failed.length < 1) {
      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Invitations sent to ${successful.length} ${pluralize('user', successful.length)}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
      return
    }

    // If any invitations failed, populate email text area with the failed email addresses.
    this.newAlert()
      .withState(AlertState.Error)
      .saying(
        `Failed to send invitations to ${failed.length} ${pluralize('user', failed.length)}.`,
      )
      .autoDismiss(2500)
      .show()
    this.setEmailAddressTextArea(failed.map((f) => f.email).join(', '))
  })

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    this.reset()
    super.close()
  }
}
